import React, { useEffect, useState } from "react";
import axios from "axios";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import ReactSlider from "react-slider";
import "./styles.css"; // keep your existing slider styles or tailwind fallback
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { FaWhatsapp, FaPhoneAlt } from "react-icons/fa";
import { CheckCircle, XCircle } from "lucide-react";
import { FaMicrophone, FaLocationArrow, FaFilter } from "react-icons/fa";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Home } from "lucide-react";


const mediaBaseUrl = "https://dnrk6bi5jdn1c.cloudfront.net/";
const CONTACT_PHONE = process.env.REACT_APP_CONTACT_PHONE;

function useDebounce(value, delay = 450) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function LocationButton({ selectedLocation, setSelectedLocation, setSearch }) {
  const [detectingLoc, setDetectingLoc] = useState(false);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setDetectingLoc(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { "User-Agent": "YourAppName/1.0" } }
          );
          const data = await res.json();
          const cityOrArea =
            data.address.city ||
            data.address.town ||
            data.address.village ||
            data.address.county;

          setSelectedLocation(cityOrArea); // update dropdown
          setSearch(cityOrArea);           // update search input
        } catch (err) {
          console.error("Error fetching address:", err);
        } finally {
          setDetectingLoc(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setDetectingLoc(false);
      }
    );
  };

  return (
    <button
      onClick={handleUseMyLocation}
      title="Use My Location"
      className="p-3 rounded-full transition-all hover:bg-green-50"
    >
      <FaLocationArrow
        size={16}
        className={`transition-transform ${detectingLoc ? "text-green-600 animate-spin" : "text-gray-500 hover:text-green-600"
          }`}
      />
    </button>
  );
}

function buildQuery(params) {
  const q = new URLSearchParams();
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v === undefined || v === null) return;
    if (Array.isArray(v) && v.length === 0) return;
    if (Array.isArray(v)) {
      v.forEach((item) => q.append(k, item));
    } else {
      q.append(k, v);
    }
  });
  return q.toString();
}

const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;

// Simple Video Modal
function VideoModal({ open, onClose, src, poster }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl overflow-hidden shadow-lg">
        <div className="flex justify-between items-center p-3 border-b">
          <h3 className="font-semibold">Property Video</h3>
          <button className="px-3 py-1" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="p-4">
          <video
            src={mediaBaseUrl + src}
            controls
            poster={poster ? mediaBaseUrl + poster : undefined}
            className="w-full max-h-[70vh] object-contain"
          />
        </div>
      </div>
    </div>
  );
}

// Description modal for full text
function DescriptionModal({ open, onClose, title, description }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-lg">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} className="px-3 py-1">Close</button>
        </div>
        <div className="p-4 text-sm text-gray-800 whitespace-pre-wrap">
          {description}
        </div>
      </div>
    </div>
  );
}

function LoginDialog({ open, onClose, onSuccess }) {
  const [mode, setMode] = useState("phone"); // 'phone' | 'email'
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;


  const submitPhone = async () => {
    if (!phone) return alert("Enter phone number");
    setLoading(true);
    try {
      await axios.post(`${API_URL}api/add-customer-details`, { contact: phone });
      onSuccess(phone);
      onClose();
    } catch (err) {
      console.error(err);
      alert("Failed to save customer details");
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async () => {
    if (!email || !password) return alert("Enter email and password");
    setLoading(true);
    try {
      await axios.post(`${API_URL}api/login-email`, { email, password });
      onSuccess(email);
      onClose();
    } catch (err) {
      console.error(err);
      alert("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h3 className="text-lg font-bold">
            {mode === "phone" ? "Login / Register" : "Login with Email"}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {mode === "phone" && (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Please enter your Phone Number
              </p>
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">
                  Phone Number
                </label>
                <div className="flex border rounded-lg overflow-hidden">
                  <span className="px-3 py-2 text-gray-600 bg-gray-50 border-r">
                    +91
                  </span>
                  <input
                    type="tel"
                    className="flex-1 px-3 py-2 focus:outline-none"
                    placeholder="Enter your number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
              </div>
              <button
                onClick={submitPhone}
                disabled={loading}
                className="w-full py-3 rounded-lg font-semibold transition
                  bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-60"
              >
                {loading ? "Saving..." : "Continue"}
              </button>

              <div className="flex items-center my-4">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="px-2 text-gray-500 text-sm">Or</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>

              <button
                onClick={() => setMode("email")}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border text-gray-700 hover:bg-gray-50"
              >
                <span className="text-lg">📧</span>
                Continue with Email/Username
              </button>
            </>
          )}

          {mode === "email" && (
            <>
              <p className="text-sm text-gray-600 mb-3">Login using Email</p>
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <button
                onClick={submitEmail}
                disabled={loading}
                className="w-full py-3 rounded-lg font-semibold transition
                  bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-60"
              >
                {loading ? "Logging in..." : "Login"}
              </button>

              <p
                onClick={() => setMode("phone")}
                className="mt-4 text-sm text-blue-600 cursor-pointer hover:underline"
              >
                ← Back to Phone Login
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t text-xs text-gray-500">
          By logging in you agree to{" "}
          <a href="#" className="text-blue-600 underline">
            Terms and Conditions
          </a>
        </div>
      </div>
    </div>
  );
}

async function getAddress(latitude, longitude) {
  const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`;

  try {
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'YourAppName/1.0' // Nominatim requires a valid User-Agent
      }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    return data.address; // Contains city, state, country, etc.
  } catch (error) {
    console.error('Error fetching address:', error);
    return null;
  }
}



const LocationDropdown = ({ selectedLocation, setSelectedLocation, locations }) => {
  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "white",
        p: 2,
        borderRadius: 3,
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        border: "1px solid #e5e7eb",
        transition: "all 0.3s ease",
        "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.12)" },
      }}
    >
      {/* Label */}
      <Box display="flex" alignItems="center" mb={1}>
        <LocationOnIcon sx={{ color: "#2563eb", mr: 1 }} />
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 400, color: "#1f2937" }}
        >
          Select Location
        </Typography>
      </Box>

      {/* Dropdown */}
      <FormControl fullWidth variant="outlined" size="medium">
        <InputLabel
          sx={{
            fontWeight: 500,
            color: "#6b7280",
            "&.Mui-focused": { color: "#2563eb" },
          }}
        >
          Location
        </InputLabel>
        <Select
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          label="Location"
          sx={{
            borderRadius: 2,
            bgcolor: "#f9fafb",
            fontWeight: 500,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#d1d5db",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#60a5fa",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#2563eb",
              borderWidth: "2px",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: 2,
                boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                mt: 1,
                bgcolor: "white",
              },
            },
          }}
        >
          <MenuItem value="">
            <em>All Locations</em>
          </MenuItem>
          {locations.map((c) => (
            <MenuItem
              key={c}
              value={c}
              sx={{
                py: 1.2,
                "&.Mui-selected": {
                  bgcolor: "#eff6ff",
                  color: "#2563eb",
                  fontWeight: 600,
                },
                "&:hover": {
                  bgcolor: "#f3f4f6",
                },
              }}
            >
              {c
                .split(" ")
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" ")}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Caption */}
      <Typography
        variant="caption"
        sx={{ color: "#6b7280", mt: 1.5, display: "block" }}
      >
        Choose your preferred city or area to explore available properties.
      </Typography>
    </Box>
  );
};


const PropertyFilters = ({
  search,
  setSearch,
  searchKey,
  setSearchKey,
  handleVoiceSearch,
  isListening,
  handleUseMyLocation,
  detectingLoc,
  selectedLocation,
  setSelectedLocation,
  locations,
  nearByOptions,
  selectedNearBy,
  setSelectedNearBy,
  minBudget,
  maxBudget,
  setMinBudget,
  setMaxBudget,
  showThumb,
  setShowThumb,
  bhkFilters,
  toggleBhk,
  resaleFilter,
  setResaleFilter,
  customFilters,
  setCustomFilters,
}) => {
  const [showFilters, setShowFilters] = useState(false);

  return (
    <>
      {/* 🔍 Search Bar — Always visible */}
      <div className="w-full bg-white rounded-2xl shadow-md p-3 sm:p-4 border border-gray-100 sticky top-0 z-20">
        <h4 className="flex items-center justify-left sm:justify-start text-base sm:text-lg font-semibold text-gray-800 gap-2 m-2">
          Find Your Dream Property
        </h4>

        <div className="flex flex-wrap sm:flex-nowrap items-stretch bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus-within:ring-2 focus-within:ring-blue-500 transition-all">
          {/* Title instead of Select */}
          <div className="w-full sm:w-auto px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-t-xl sm:rounded-l-xl sm:rounded-tr-none border-b sm:border-b-0 sm:border-r border-gray-200 flex items-center">
            City
          </div>

          {/* Search Input */}
          <input
            type="text"
            placeholder="Search by area, keyword..."
            className="flex-1 min-w-[140px] px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* 🎙️ Voice Button */}
          <button
            onClick={handleVoiceSearch}
            title="Voice Search"
            className={`flex items-center justify-center p-2 border-t sm:border-t-0 sm:border-l border-gray-200 transition-all ${isListening ? "text-red-500 animate-pulse" : "text-gray-500 hover:text-blue-600"
              }`}
          >
            <FaMicrophone size={18} />
          </button>

          {/* 📍 Location Button */}
          <LocationButton
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            setSearch={setSearch}  // ✅ keep this
          />
        </div>

        {/* Mobile Filter Button */}
        <div className="flex justify-left mt-3 sm:hidden">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-full shadow-md hover:bg-blue-700 transition"
          >
            <FaFilter /> {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      </div>

      {/* 🧩 Filters — Collapsible on mobile, visible on desktop */}
      <aside
        className={`${showFilters ? "max-h-[2000px] opacity-100 mt-4" : "max-h-0 opacity-0 sm:max-h-none sm:opacity-100"
          } overflow-hidden transition-all duration-500 ease-in-out w-full lg:w-72 bg-gradient-to-b from-white/90 to-white/70 backdrop-blur-md rounded-2xl shadow-xl p-4 sm:p-5 space-y-6 border border-gray-100`}
      >
        {/* 🌆 Enhanced Location Filter */}
        <LocationDropdown
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          locations={["Solapur", "Jule Solapur", "Aasra Chowk"]}
        />


        {/* 🏙️ Nearby Amenities */}
        {nearByOptions?.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nearby / Amenities</label>
            <div className="flex flex-wrap gap-2">
              {nearByOptions.map((n) => {
                const active = selectedNearBy.includes(n);
                return (
                  <button
                    key={n}
                    onClick={() =>
                      setSelectedNearBy((prev) =>
                        prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]
                      )
                    }
                    className={`px-3 py-1.5 text-sm rounded-full shadow-sm border transition-all ${active
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-blue-50 hover:border-blue-300"
                      }`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 💰 Budget */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Budget (₹ L)</label>
          <ReactSlider
            className="horizontal-slider"
            thumbClassName="thumb"
            trackClassName="track"
            min={0}
            max={2000}
            value={[minBudget, maxBudget]}
            onChange={(vals) => {
              setMinBudget(vals[0]);
              setMaxBudget(vals[1]);
            }}
            onBeforeChange={() => setShowThumb(true)}
            onAfterChange={() => setShowThumb(false)}
            pearling
            minDistance={1}
            renderThumb={(props, state) => (
              <div {...props} className="thumb relative">
                {showThumb && (
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs px-2 py-0.5 rounded-md shadow-md">
                    ₹{state.valueNow} L
                  </div>
                )}
              </div>
            )}
          />
          <div className="flex justify-between text-xs text-gray-600 mt-2">
            <span>₹{minBudget} L</span>
            <span>{maxBudget === 2000 ? "₹2000 L+" : `₹${maxBudget} L`}</span>
          </div>
        </div>

        {/* 🏡 BHK */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms</label>
          <div className="flex gap-2 flex-wrap">
            {["1", "2", "3", "4"].map((n) => (
              <button
                key={n}
                onClick={() => toggleBhk(n)}
                className={`px-4 py-1.5 rounded-full text-sm transition-all shadow-sm ${bhkFilters[n]
                  ? "bg-blue-600 text-white"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-blue-50"
                  }`}
              >
                {n} BHK
              </button>
            ))}
          </div>
        </div>

        {/* 🔁 Resale */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setResaleFilter((s) => (s === true ? null : true))}
              className={`px-3 py-1.5 rounded-full text-sm shadow-sm ${resaleFilter === true
                ? "bg-blue-600 text-white"
                : "bg-gray-50 text-gray-700 hover:bg-blue-50"
                }`}
            >
              Resale
            </button>
            <button
              onClick={() => setResaleFilter((s) => (s === false ? null : false))}
              className={`px-3 py-1.5 rounded-full text-sm shadow-sm ${resaleFilter === false
                ? "bg-blue-600 text-white"
                : "bg-gray-50 text-gray-700 hover:bg-blue-50"
                }`}
            >
              New
            </button>
          </div>
        </div>

        {/* 🏦 Loan Options */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Loan Options</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCustomFilters((s) => ({ ...s, homeLoan: s.homeLoan ? null : true }))}
              className={`px-3 py-1.5 rounded-full text-sm shadow-sm ${customFilters.homeLoan
                ? "bg-blue-600 text-white"
                : "bg-gray-50 text-gray-700 hover:bg-blue-50"
                }`}
            >
              Home Loan
            </button>
            <button
              onClick={() => setCustomFilters((s) => ({ ...s, resale: s.resale ? null : true }))}
              className={`px-3 py-1.5 rounded-full text-sm shadow-sm ${customFilters.resale
                ? "bg-blue-600 text-white"
                : "bg-gray-50 text-gray-700 hover:bg-blue-50"
                }`}
            >
              Resale
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};



export default function ExploreProperties() {
  const [page, setPage] = useState(1);
  const [properties, setProperties] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showThumb, setShowThumb] = useState(false); // <-- controls visibility

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const searchDebounced = useDebounce(search);

  const [searchKey, setSearchKey] = useState("name"); // title/location/description
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedNearBy, setSelectedNearBy] = useState([]);
  const [minBudget, setMinBudget] = useState(0);
  const [maxBudget, setMaxBudget] = useState(2000);
  const [bhkFilters, setBhkFilters] = useState({ "1": false, "2": false, "3": false, "4": false });
  const [rentalFilter, setRentalFilter] = useState(null); // "rent" | "sale" | null
  const [resaleFilter, setResaleFilter] = useState(null); // true | false | null
  const [customFilters, setCustomFilters] = useState({ homeLoan: null, resale: null });

  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingLike, setPendingLike] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const [locations, setLocations] = useState([]);
  const [nearByOptions, setNearByOptions] = useState([]);

  const minBudgetDebounced = useDebounce(minBudget, 500);
  const maxBudgetDebounced = useDebounce(maxBudget, 500);

  const [videoOpenFor, setVideoOpenFor] = useState(null);
  const [descOpenFor, setDescOpenFor] = useState(null);


  const [isListening, setIsListening] = useState(false);
  const [detectingLoc, setDetectingLoc] = useState(false);


  const handleVoiceSearch = () => {
    if (!("webkitSpeechRecognition" in window)) {
      alert("Sorry, your browser does not support voice input.");
      return;
    }
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = "en-IN";
    recognition.start();
    setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearch(transcript);
      setIsListening(false);
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
  };

  // 📍 Use Device Location Handler
  const handleUseMyLocation = async () => {
    setDetectingLoc(true);
    try {
      const { city, area } = await getUserLocation(); // your location API
      const locationName = area || city;
      setSelectedLocation(locationName); // update dropdown
    } catch (err) {
      console.error(err);
    } finally {
      setDetectingLoc(false);
    }
  };



  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);


  useEffect(() => {
    setPage(1);
  }, [
    searchDebounced,
    searchKey,
    selectedLocation,
    selectedNearBy,
    minBudgetDebounced,
    maxBudgetDebounced,
    bhkFilters,
    rentalFilter,
    resaleFilter,
    customFilters,
  ]);
  function toTitleCase(str) {
    return str
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const filter = {};

        const bhkFromUrl = searchParams.get("bhk");
        const directionFromUrl = searchParams.get("direction");

        const propertyType = searchParams.get("propertyType");


        if (bhkFromUrl) filter.bhk = [bhkFromUrl];
        if (directionFromUrl) filter.direction = directionFromUrl;
        if (propertyType) filter.propertyType = propertyType;

        if (searchDebounced) {
          filter.search = searchDebounced;
          filter.searchKey = searchKey;
        }
        if (selectedLocation) filter.city = selectedLocation;
        if (selectedNearBy.length) filter.nearBy = selectedNearBy;

        filter.minRate = minBudgetDebounced;
        filter.maxRate = maxBudgetDebounced;

        const selectedBHKs = Object.keys(bhkFilters).filter((k) => bhkFilters[k]);
        if (selectedBHKs.length) filter.bhk = selectedBHKs;

        if (rentalFilter !== null) filter.rentalType = rentalFilter; // expects API to understand "rent" or "sale"
        if (resaleFilter !== null) filter.resale = resaleFilter;

        if (customFilters.homeLoan !== null) filter.homeLoan = customFilters.homeLoan;
        if (customFilters.resale !== null) filter.resale = customFilters.resale;

        filter.page = page;
        filter.limit = 10;

        const qs = buildQuery(filter);
        const res = await axios.get(`${API_URL}api/get-properties?${qs}`);

        if (res.data.success) {
          setProperties(res.data.data || []);
          setTotalPages(res.data.totalPages || 1);

          const locs = new Set();
          const nb = new Set();
          (res.data.data || []).forEach((p) => {
            if (p.city) locs.add(p.city);
            (p.nearBy || []).forEach((n) => nb.add(n));
          });

          setLocations([...locs].slice(0, 6));
          setNearByOptions((prev) => (prev.length === 0 ? [...nb] : prev));
          setTotalCount(res.data.totalCount); // ← ✅ store total count

        } else {
          setProperties([]);
          setTotalPages(1);
        }
      } catch (err) {
        console.error(err);
        setProperties([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [
    page,
    searchDebounced,
    searchKey,
    selectedLocation,
    selectedNearBy,
    minBudgetDebounced,
    maxBudgetDebounced,
    bhkFilters,
    rentalFilter,
    resaleFilter,
    customFilters,
    location.search

  ]);


  function slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")      // replace spaces & special chars with hyphen
      .replace(/^-+|-+$/g, "");          // trim hyphens from start & end
  }



  const toggleBhk = (n) => setBhkFilters((s) => ({ ...s, [n]: !s[n] }));

  const onLikeClick = (propertyId) => {
    if (!currentUser) {
      setPendingLike(propertyId);
      setLoginOpen(true);
      return;
    }
    doLike(propertyId, currentUser);
  };

  const onLoginSuccess = async (phoneOrEmail) => {
    setCurrentUser(phoneOrEmail);
    if (pendingLike) {
      await doLike(pendingLike, phoneOrEmail);
      setPendingLike(null);
    }
  };

  const doLike = async (propertyId, likedBy) => {
    try {
      setProperties((prev) => prev.map((p) => (p._id.$oid === propertyId ? { ...p, __liked: true } : p)));
      await axios.post(`${API_URL}api/add-customer-details`, { contact: likedBy, source: "web_like" });
      await axios.post(`${API_URL}api/liked-properties`, { likedPropertyId: propertyId, likedBy });
    } catch (err) {
      console.error(err);
      setProperties((prev) => prev.map((p) => (p._id.$oid === propertyId ? { ...p, __liked: false } : p)));
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 p-4 max-w-7xl mx-auto" style={{ backgroundColor: '#f2f2f2' }}>
      {/* Sidebar */}
      <div>
        <PropertyFilters
          search={search}
          setSearch={setSearch}
          searchKey={searchKey}
          setSearchKey={setSearchKey}
          handleVoiceSearch={handleVoiceSearch}
          isListening={isListening}
          handleUseMyLocation={handleUseMyLocation}
          detectingLoc={detectingLoc}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          locations={locations}
          nearByOptions={nearByOptions}
          selectedNearBy={selectedNearBy}
          setSelectedNearBy={setSelectedNearBy}
          minBudget={minBudget}
          maxBudget={maxBudget}
          setMinBudget={setMinBudget}
          setMaxBudget={setMaxBudget}
          showThumb={showThumb}
          setShowThumb={setShowThumb}
          bhkFilters={bhkFilters}
          toggleBhk={toggleBhk}
          resaleFilter={resaleFilter}
          setResaleFilter={setResaleFilter}
          customFilters={customFilters}
          setCustomFilters={setCustomFilters}
        />
      </div>


      {/* Main */}
      <main className="flex-1">

         {/* ✅ Commission Info */}
      <div className="w-full bg-yellow-100 text-yellow-800 px-4 py-2 rounded-xl mb-4 text-center font-medium shadow-sm">
        Our business operates at a 2% commission.
      </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{properties.length || totalCount} Properties</h2>
          <div className="text-sm text-gray-600">Page {page} / {totalPages}</div>
        </div>

        {loading ? (
          <div className="text-center">
            <div role="status">
              <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )
          : properties.length === 0 ? (
            <div className="text-center py-20">No properties found</div>
          ) : (
            <div className="grid gap-6 grid-cols-1">
              {properties.map((p) => {
                const id = p._id?.$oid || p._id || Math.random().toString(36).slice(2);
                const ownerDisplay =
                  (p.owner && p.owner.toLowerCase() === "admin") || p.created_by === "admin"
                    ? "Skyline Properties"
                    : p.created_by || p.owner || "Unknown";

                const whatsappMessage = `Hello! I’m looking for ${p.name}. Can we connect to discuss and negotiate a mutually beneficial arrangement?`;
                const whatsappUrl = `https://wa.me/+919373733473?text=${encodeURIComponent(whatsappMessage)}`;



                return (
                  <div
                    key={id}
                    className={`bg-white rounded-2xl shadow p-3 flex flex-col md:flex-row overflow-hidden border ${!(!p.status || p.status === "Available") ? "sold-blur" : ""}`}
                  >
                    {/* Left: Image carousel */}
                    <div
                      className="w-full md:w-60 flex-shrink-0 rounded-lg overflow-hidden relative"
                      key={id}
                      onClick={() => {
                        navigate(`/properties/${slugify(p.name || "property")}-${id}`)
                      }}
                    >
                      <Carousel
                        showThumbs={false}
                        showStatus={false}
                        infiniteLoop={false}
                        autoPlay={false}
                        swipeable
                        emulateTouch
                        className="property-carousel"
                      >
                        {p.imageIds && p.imageIds.length > 0 ? (
                          p.imageIds.map((img, i) => (
                            <div key={i} className="h-44">
                              <img
                                src={mediaBaseUrl + img}
                                alt={`${p.name} ${i + 1}`}
                                className="object-cover h-44 w-full"
                              />
                            </div>
                          ))
                        ) : (
                          <div className="h-44 bg-gray-100 flex items-center justify-center text-sm text-gray-500">
                            No image
                          </div>
                        )}
                      </Carousel>
                      {!(!p.status || p.status === "Available") && (
                        <div className="absolute inset-0 flex items-center justify-center text-4xl font-bold text-red-600 bg-white/50 rounded-2xl pointer-events-none">
                          SOLD
                        </div>
                      )}

                      {/* ✅ Status Badge (High Demand / Available / Sold etc.) */}
                      <div
                        className={`absolute bottom-2 left-2 z-20 flex items-center gap-1.5 
      px-2.5 py-1 rounded-full font-semibold shadow-md
      text-xs sm:text-sm
      ${!p.status || p.status === "Available"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                          }`}
                      >
                        {!p.status || p.status === "Available" ? (
                          <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        )}
                        <span>
                          {(!p.status || p.status === "Available")
                            ? "High Demand"
                            : p.status}
                        </span>
                      </div>

                      {/* Featured badge */}
                      {p.featured && (
                        <div className="absolute top-2 left-2 bg-purple-700 text-white text-xs px-2 py-1 rounded">
                          FEATURED
                        </div>
                      )}

                      {/* Sale type badge */}
                      {p.saleType && (
                        <div
                          className={`absolute top-2 right-2 rounded-full px-3 py-1 text-xs font-semibold shadow
        ${p.saleType === "Resale"
                              ? "bg-orange-600 text-white"
                              : "bg-green-600 text-white"
                            }`}
                        >
                          {p.saleType}
                        </div>
                      )}
                    </div>


                    {/* Right Section */}
                    <div className="flex-1 flex flex-col p-3" >
                      {/* Top Row: Title and like button */}
                      <div className="flex justify-between items-start" key={id}
                        onClick={() => {
                          navigate(`/properties/${slugify(p.name || "property")}-${id}`)
                        }}>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">
                            {toTitleCase(p.name) || "—"}
                          </h3>

                          <p className="text-sm text-gray-600">
                            {p.location
                              ? p.city &&
                                !p.location.toLowerCase().includes(p.city.toLowerCase())
                                ? `${p.location}, ${p.city}`
                                : p.location
                              : p.city || ""}
                          </p>
                        </div>


                      </div>

                      {/* Price / Area / BHK */}
                      <div className="mt-3 flex flex-wrap gap-4 text-sm" onClick={() => {
                        navigate(`/properties/${slugify(p.name || "property")}-${id}`)
                      }}>
                        <div>
                          <div className="text-gray-500">{p.rate ? 'Rate' : 'Rent'}</div>
                          <div className="font-bold text-lg">
                            {p.rate ? `₹${p.rate} ${p.priceUnit || ""}` : `₹${p.rent} ${p.rentUnit || ""}`}
                          </div>
                          {p.ratePerSqft && (
                            <div className="text-xs text-gray-400">₹{p.ratePerSqft}/Sqft</div>
                          )}
                        </div>

                        <div>
                          <div className="text-gray-500">Area</div>
                          <div className="font-bold text-lg">
                            {p.builtUp
                              ? `${p.builtUp} Sqft (Built-up)`
                              : p.carpetArea
                                ? `${p.carpetArea} Sqft (Carpet)`
                                : p.plotArea
                                  ? `${p.plotArea} ${p.plotUnit || "Sqft"}`
                                  : "—"}
                          </div>
                        </div>

                        {p.bhkCount > 0 &&
                          <div>
                            <div className="text-gray-500">{p.bhkType}</div>
                            <div className="font-bold text-lg">
                              {p.bhkCount
                                ? `${p.bhkCount} ${p.bhkType}`
                                : null}
                            </div>
                          </div>}

                        {p.bhk && (
                          <div>
                            <div className="text-gray-500">Configuration</div>
                            <div className="font-bold text-lg">{p.bhk} BHK</div>
                          </div>
                        )}
                      </div>

                      {/* Highlights */}
                      {p.highlights && p.highlights.length > 0 && (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {p.highlights.slice(0, 3).map((h, idx) => (
                            <span
                              key={idx}
                              className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                            >
                              {h}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Nearby */}
                      {p.nearBy && p.nearBy.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {p.nearBy.slice(0, 4).map((n, idx) => (
                            <span
                              key={idx}
                              className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded border border-blue-200"
                            >
                              {n}
                            </span>
                          ))}
                          {p.nearBy.length > 4 && (
                            <span className="text-xs text-gray-500">
                              +{p.nearBy.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                      {/* Description */}
                      {/* <div className="mt-3 text-sm text-gray-700">
                        {p.description
                          ? p.description.length > 120
                            ? p.description.slice(0, 120) + "..."
                            : p.description
                          : "No description"}
                        {p.description && p.description.length > 120 && (
                          <button
                            onClick={() => setDescOpenFor(id)}
                            className="text-blue-600 text-sm ml-2"
                          >
                            See more
                          </button>
                        )}
                      </div> */}

                      <br />
                      {/* VIDEO  */}

                      {p.videoId && (
                        <button
                          onClick={() => setVideoOpenFor(p.videoId)}
                          className="
        self-start           /* left aligned */
        px-2 py-1            /* tighter padding inside button */
        rounded-lg
        border border-blue-600 text-blue-600
        hover:bg-blue-50
        active:scale-95 transition
        font-medium text-sm shadow-sm
      "
                        >
                          ▶ Watch Video
                        </button>
                      )}






                      {/* Dealer & Actions */}
                      <div className="mt-4 flex flex-col gap-2">
                        {/* Buttons on second last line */}
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => window.open(`tel:${CONTACT_PHONE}`)}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl shadow-sm 
         bg-gradient-to-r from-blue-600 to-indigo-600 text-white 
         hover:shadow-md hover:scale-105 transition text-sm whitespace-nowrap"
                          >
                            <FaPhoneAlt className="text-lg" />

                            Contact Now
                          </button>

                          <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 rounded-xl shadow-sm 
         bg-gradient-to-r from-green-500 to-green-600 text-white 
         hover:shadow-md hover:scale-105 transition text-sm whitespace-nowrap"
                          >
                            <FaWhatsapp className="text-lg" />
                            WhatsApp
                          </a>
                        </div>

                        {/* Dealer on last line */}

                      </div>


                    </div>

                    <div className="flex justify-end text-xs text-gray-500">
                      Dealer •{" "}
                      <span className="text-green-600 ml-1">
                        {p.created_by === "ADMIN" ? "Skyline Properties" : p.created_by}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        {/* Pagination */}
        <div className="mt-6 flex justify-between items-center">
          <button
            className="px-3 py-1 border rounded"
            onClick={() => {
              setPage((p) => {
                const newPage = Math.max(1, p - 1);
                window.scrollTo({ top: 0, behavior: "smooth" });
                return newPage;
              });
            }}
            disabled={page <= 1}
          >
            Prev
          </button>

          <div className="text-sm">Page {page} of {totalPages}</div>

          <button
            className="px-3 py-1 border rounded"
            onClick={() => {
              setPage((p) => {
                const newPage = Math.min(totalPages, p + 1);
                window.scrollTo({ top: 0, behavior: "smooth" });
                return newPage;
              });
            }}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </main>

      {/* Global modals */}
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} onSuccess={onLoginSuccess} />

      {videoOpenFor && (
        <VideoModal
          open={!!videoOpenFor}
          onClose={() => setVideoOpenFor(null)}
          src={videoOpenFor} // directly using videoId now
        />
      )}


      {descOpenFor && (
        <DescriptionModal
          open={!!descOpenFor}
          onClose={() => setDescOpenFor(null)}
          title={properties.find((x) => (x._id?.$oid || x._id) === descOpenFor)?.name || "Description"}
          description={properties.find((x) => (x._id?.$oid || x._id) === descOpenFor)?.description || "No description"}
        />
      )}
    </div>
  );
}


//  <button
//                         onClick={() => onLikeClick(id)}
//                         className={`
//   relative
//   w-10 h-10
//   rounded-full
//   border
//   flex items-center justify-center
//   transition-all duration-300
//   shadow-sm
//   ${p.__liked
//                             ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md scale-105"
//                             : "bg-white text-gray-400 hover:text-pink-500 hover:border-pink-400"}
// `}
//                       >
//                         <span className="text-xl leading-none select-none">
//                           {p.__liked ? "♥" : "♡"}
//                         </span>

//                         {/* pulse animation when liked */}
//                         {p.__liked && (
//                           <span className="absolute inset-0 rounded-full bg-pink-400/20 animate-ping"></span>
//                         )}
//                       </button>
